import { useCallback } from 'react';
import { useMemoryDB } from './useMemoryDB';

export interface RetrievedMemory {
  content: string;
  type: 'profile' | 'event' | 'relation';
  timestamp: number;
  relevance: number;
  emotionTag?: string;
  topicTag?: string;
}

// トピックグループ定義
const TOPIC_GROUPS: Record<string, string[]> = {
  work: ['仕事', '会社', '上司', '残業', '職場', '同僚', '部下', '転職', '昇進', '給料', 'バイト', '社畜', 'クエスト', 'ダンジョン', 'ボス'],
  hobby: ['趣味', 'ゲーム', 'アニメ', '漫画', '映画', '音楽', '読書', 'スポーツ', '料理', '旅行', 'HP', 'MP', 'レベル'],
  relationship: ['友達', '家族', '恋人', '彼氏', '彼女', '親', '兄弟', '姉妹', '先輩', '後輩', '先生'],
  health: ['体調', '疲れ', '眠い', '風邪', '病院', '睡眠', '食事', '運動'],
  emotion: ['嬉しい', '悲しい', '不安', '怖い', '楽しい', '辛い', 'しんどい', '寂しい', 'イライラ'],
  event: ['誕生日', '記念日', '試験', '面接', '旅行', '引っ越し', '結婚', '卒業'],
};

// 感情タグ抽出
function detectEmotionTag(text: string): string | undefined {
  if (/疲れ|しんどい|つらい|辛い|きつい/.test(text)) return 'tired';
  if (/嬉しい|楽しい|最高|幸せ|良かった/.test(text)) return 'happy';
  if (/悲しい|寂しい|泣|切ない/.test(text)) return 'sad';
  if (/不安|心配|怖い|緊張/.test(text)) return 'anxious';
  if (/怒|イライラ|むかつく|腹立/.test(text)) return 'angry';
  return undefined;
}

// トピックタグ抽出
function detectTopicTag(text: string): string | undefined {
  for (const [topic, keywords] of Object.entries(TOPIC_GROUPS)) {
    if (keywords.some((kw) => text.includes(kw))) return topic;
  }
  return undefined;
}

// 2つのテキスト間のトピック類似度（0〜1）
function calcTopicSimilarity(textA: string, textB: string): number {
  let score = 0;
  for (const keywords of Object.values(TOPIC_GROUPS)) {
    const aHas = keywords.some((kw) => textA.includes(kw));
    const bHas = keywords.some((kw) => textB.includes(kw));
    if (aHas && bHas) score += 1;
  }
  return Math.min(score / 2, 1);
}

// 文字レベルのキーワードマッチスコア
function calcKeywordScore(query: string, content: string): number {
  // 2文字以上の単語を抽出
  const tokens = query.match(/[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF]{2,}/g) || [];
  let score = 0;
  tokens.forEach((token) => {
    if (content.includes(token)) score += 1;
  });
  return score;
}

// 時系列スコア（新しいほど高い）
function calcRecencyScore(timestamp: number): number {
  const daysSince = (Date.now() - timestamp) / (1000 * 60 * 60 * 24);
  if (daysSince < 1) return 3;
  if (daysSince < 3) return 2;
  if (daysSince < 7) return 1;
  if (daysSince < 30) return 0.5;
  return 0.2;
}

// 重要度スコア
function calcTypeScore(type: 'profile' | 'event' | 'relation'): number {
  if (type === 'relation') return 1.5;
  if (type === 'event') return 1.2;
  return 1.0;
}

export function useMemoryRetrieval() {
  const { getMemories } = useMemoryDB();

  // 記憶を自然な会話文に変換（文脈に応じたバリエーション）
  const formatMemoryAsConversation = useCallback((
    memory: RetrievedMemory,
    context: 'followup' | 'recall' | 'casual' = 'recall'
  ): string => {
    const content = memory.content;

    if (context === 'followup') {
      // フォローアップ：前回の話の続きを聞く
      const patterns = [
        `そういえば、${content}って言ってましたよね。その後どうなりました？`,
        `${content}のこと、気になってたんです。どうでした？`,
        `前に${content}って話してましたよね。うまくいきましたか？`,
        `${content}って言ってたの、覚えてます。最近はどうですか？`,
      ];
      return patterns[Math.floor(Math.random() * patterns.length)];
    }

    if (context === 'casual') {
      // カジュアル：さりげなく記憶を織り込む
      const patterns = [
        `${content}でしたよね。`,
        `確か${content}って言ってましたね。`,
        `${content}のこと、覚えてますよ。`,
      ];
      return patterns[Math.floor(Math.random() * patterns.length)];
    }

    // recall：自然な想起
    if (memory.type === 'event') {
      const patterns = [
        `${content}って言ってましたよね。`,
        `この前、${content}って話してましたね。`,
        `${content}のこと、ちゃんと覚えてます。`,
      ];
      return patterns[Math.floor(Math.random() * patterns.length)];
    }
    if (memory.type === 'relation') {
      const patterns = [
        `${content}なんですよね。`,
        `${content}って聞いてました。`,
        `${content}でしたよね。`,
      ];
      return patterns[Math.floor(Math.random() * patterns.length)];
    }
    // profile
    const patterns = [
      `${content}でしたよね。`,
      `確か${content}って言ってました。`,
    ];
    return patterns[Math.floor(Math.random() * patterns.length)];
  }, []);

  // ユーザーメッセージに関連する記憶を多角的に検索
  const retrieveRelevantMemories = useCallback(async (
    userMessage: string,
    limit: number = 3
  ): Promise<RetrievedMemory[]> => {
    const allMemories = await getMemories();
    if (allMemories.length === 0) return [];

    const queryEmotion = detectEmotionTag(userMessage);
    const queryTopic = detectTopicTag(userMessage);

    const scored = allMemories.map((mem) => {
      const memEmotion = detectEmotionTag(mem.content);
      const memTopic = detectTopicTag(mem.content);

      // キーワードスコア
      const keywordScore = calcKeywordScore(userMessage, mem.content);
      // トピック類似度
      const topicScore = calcTopicSimilarity(userMessage, mem.content) * 2;
      // 感情一致ボーナス
      const emotionBonus = queryEmotion && memEmotion === queryEmotion ? 1.5 : 0;
      // トピックタグ一致ボーナス
      const topicTagBonus = queryTopic && memTopic === queryTopic ? 1.0 : 0;
      // 時系列スコア
      const recencyScore = calcRecencyScore(mem.timestamp);
      // タイプスコア
      const typeScore = calcTypeScore(mem.type);

      const totalRelevance =
        (keywordScore + topicScore + emotionBonus + topicTagBonus) * typeScore + recencyScore;

      return {
        content: mem.content,
        type: mem.type,
        timestamp: mem.timestamp,
        relevance: totalRelevance,
        emotionTag: memEmotion,
        topicTag: memTopic,
      } as RetrievedMemory;
    });

    return scored
      .filter((m) => m.relevance > 0.5)
      .sort((a, b) => b.relevance - a.relevance)
      .slice(0, limit);
  }, [getMemories]);

  // 最近の記憶を取得（時系列順）
  const getRecentMemories = useCallback(async (limit: number = 5): Promise<RetrievedMemory[]> => {
    const allMemories = await getMemories();
    return allMemories
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, limit)
      .map((mem) => ({
        content: mem.content,
        type: mem.type,
        timestamp: mem.timestamp,
        relevance: 1,
        emotionTag: detectEmotionTag(mem.content),
        topicTag: detectTopicTag(mem.content),
      }));
  }, [getMemories]);

  // 昨日の記憶を取得
  const getYesterdayMemories = useCallback(async (): Promise<RetrievedMemory[]> => {
    const allMemories = await getMemories();
    const now = Date.now();
    const oneDayAgo = now - 24 * 60 * 60 * 1000;
    const twoDaysAgo = now - 48 * 60 * 60 * 1000;

    return allMemories
      .filter((mem) => mem.timestamp >= twoDaysAgo && mem.timestamp < oneDayAgo)
      .map((mem) => ({
        content: mem.content,
        type: mem.type,
        timestamp: mem.timestamp,
        relevance: 1,
        emotionTag: detectEmotionTag(mem.content),
        topicTag: detectTopicTag(mem.content),
      }));
  }, [getMemories]);

  // 同じトピックの記憶を取得（フォローアップ用）
  const getTopicMemories = useCallback(async (
    topic: string,
    limit: number = 3
  ): Promise<RetrievedMemory[]> => {
    const allMemories = await getMemories();
    const keywords = TOPIC_GROUPS[topic] || [];

    return allMemories
      .filter((mem) => keywords.some((kw) => mem.content.includes(kw)))
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, limit)
      .map((mem) => ({
        content: mem.content,
        type: mem.type,
        timestamp: mem.timestamp,
        relevance: 1,
        topicTag: topic,
      }));
  }, [getMemories]);

  // 記憶を会話に織り込むべきか判定
  const shouldWeaveMemory = useCallback(async (messageCount: number): Promise<boolean> => {
    if (messageCount % 3 === 0 || messageCount % 5 === 0) {
      return Math.random() < 0.3;
    }
    return false;
  }, []);

  // ランダムな記憶を1つ取得して会話文に変換
  const getRandomMemoryPhrase = useCallback(async (): Promise<string | null> => {
    const recent = await getRecentMemories(10);
    if (recent.length === 0) return null;
    const randomMemory = recent[Math.floor(Math.random() * recent.length)];
    return formatMemoryAsConversation(randomMemory, 'casual');
  }, [getRecentMemories, formatMemoryAsConversation]);

  return {
    retrieveRelevantMemories,
    getRecentMemories,
    getYesterdayMemories,
    getTopicMemories,
    formatMemoryAsConversation,
    shouldWeaveMemory,
    getRandomMemoryPhrase,
    detectTopicTag,
    detectEmotionTag,
  };
}
